import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Card } from '../../components/card'
import IndexHeaderRow from '../common/indexes/indexHeaderRow'
import TablePagination from '../../components/tablePagination'
import LoadingSection from '../../components/loadingSection'
import { ModalWrapper } from '../../components/modalWrapper'
import { displayNotification } from '../../actions/notificationsActions'
import { getApiRequest, postApiRequest } from '../../helpers/apiHelper'
import { Note, User } from '../../core/types'

const PAGE_SIZE = 10
const DEFAULT_SORT = 'created_at'
const DEFAULT_DIRECTION = 'DESC'

interface Props {
  user: User
}

interface NoteResponse {
  id: number
  text: string
  created_at: string
  author_full_name: string
  is_flag: boolean
  notable_type: string
  notable_id: number
  author_id: number
}

interface NotesResponse {
  notes?: NoteResponse[]
  meta?: {
    total?: number
    limit?: number
    offset?: number
  }
}

const headers = [
  { displayHeader: 'Flagged', sortEnum: 'flagged', sortable: true },
  { displayHeader: 'Author', sortEnum: 'author', sortable: true },
  { displayHeader: 'Created', sortEnum: 'created_at', sortable: true },
  { displayHeader: 'Notes', sortable: false },
  { displayHeader: 'Type', sortEnum: 'type', sortable: true },
]

const mapResponseToNote = (response: NoteResponse): Note => ({
  id: response.id,
  text: response.text || '',
  createdAt: response.created_at,
  authorFullName: response.author_full_name,
  isFlag: Boolean(response.is_flag),
  notableType: response.notable_type,
  notableId: response.notable_id,
  authorId: response.author_id,
})

const formatDate = (value?: string) => {
  if (!value) return '--'

  const date = new Date(value)
  if (isNaN(date.getTime())) return '--'

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatTime = (value?: string) => {
  if (!value) return '--'

  const date = new Date(value)
  if (isNaN(date.getTime())) return '--'

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

const truncateText = (value?: string) => {
  if (!value) return ''
  if (value.length <= 100) return value
  return `${value.slice(0, 100)}...`
}

const displayType = (notableType?: string) => {
  if (!notableType) return '--'
  return notableType.toLowerCase() === 'user' ? 'People' : notableType
}

const buildSearchString = (offset: number) => `?limit=${PAGE_SIZE}&offset=${offset}`

export const UserNotes: React.FC<Props> = ({ user }) => {
  const dispatch = useDispatch()
  const [notes, setNotes] = useState<Note[]>([])
  const [meta, setMeta] = useState<{ total: number; limit: number; offset: number }>({
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [direction, setDirection] = useState<'ASC' | 'DESC'>(DEFAULT_DIRECTION)
  const [modalMode, setModalMode] = useState<'create' | 'view' | null>(null)
  const [draftText, setDraftText] = useState('')
  const [draftFlagged, setDraftFlagged] = useState(false)
  const [selectedNote, setSelectedNote] = useState<Note | null>(null)

  const userId = useMemo(() => Number(user?.id), [user?.id])

  const totalPages = useMemo(() => {
    if (!meta.total) return 0
    return Math.ceil(meta.total / PAGE_SIZE)
  }, [meta.total])

  const searchString = useMemo(
    () => buildSearchString(currentPage * PAGE_SIZE),
    [currentPage]
  )

  const fetchNotes = useCallback(
    async (pageToLoad: number) => {
      if (!userId) return
      setIsLoading(true)
      const offset = pageToLoad * PAGE_SIZE
      const params = new URLSearchParams({
        notable_id: userId.toString(),
        notable_type: 'User',
        limit: PAGE_SIZE.toString(),
        offset: offset.toString(),
        sort,
        direction,
      })

      try {
        const { data } = await getApiRequest(`notes?${params.toString()}`)
        const response: NotesResponse = data || {}
        const rawNotes = Array.isArray(response.notes) ? response.notes : []
        setNotes(rawNotes.map(mapResponseToNote))
        const responseMeta = response.meta || {}
        setMeta({
          total: responseMeta.total ?? rawNotes.length,
          limit: responseMeta.limit ?? PAGE_SIZE,
          offset: responseMeta.offset ?? offset,
        })
      } catch (error) {
        dispatch(displayNotification('Failed to load notes', 'error'))
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, direction, sort, userId]
  )

  useEffect(() => {
    setCurrentPage(0)
  }, [userId, sort, direction])

  useEffect(() => {
    fetchNotes(currentPage)
  }, [currentPage, fetchNotes])

  const handleSortChange = (sortEnum: string) => {
    if (sortEnum === sort) {
      setDirection(direction === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSort(sortEnum)
      setDirection(sortEnum === 'created_at' ? 'DESC' : 'ASC')
    }
  }

  const handleAddNote = async () => {
    const trimmed = draftText.trim()
    if (!trimmed) {
      dispatch(displayNotification('Note text is required', 'error'))
      return
    }

    if (!userId) {
      dispatch(displayNotification('Unable to determine user for note', 'error'))
      return
    }

    setIsSubmitting(true)
    try {
      await postApiRequest('notes', {
        text: trimmed,
        is_flag: draftFlagged,
        notable_id: userId,
        notable_type: 'User',
      })
      dispatch(displayNotification('Note added'))
      setModalMode(null)
      setDraftText('')
      setDraftFlagged(false)
      setCurrentPage(0)
      fetchNotes(0)
    } catch (error) {
      dispatch(displayNotification('Failed to add note', 'error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const closeModal = () => {
    setModalMode(null)
    setSelectedNote(null)
    setDraftText('')
    setDraftFlagged(false)
  }

  const openCreateModal = () => {
    setModalMode('create')
    setDraftText('')
    setDraftFlagged(false)
  }

  const openViewModal = (note: Note) => {
    setSelectedNote(note)
    setModalMode('view')
  }

  const handlePrevPage = () => {
    if (currentPage === 0) return
    setCurrentPage(currentPage - 1)
  }

  const handleNextPage = () => {
    if (totalPages === 0) return
    if (currentPage >= totalPages - 1) return
    setCurrentPage(currentPage + 1)
  }

  const handleSelectPage = (pageNumber: number) => {
    setCurrentPage(pageNumber - 1)
  }

  const renderRows = () => {
    if (notes.length === 0) {
      return (
        <tr className="index-table__row">
          <td className="index-table__cell" colSpan={5}>
            <em>No notes yet</em>
          </td>
        </tr>
      )
    }

    return notes.map((note) => {
      const flagClass = note.isFlag
        ? 'user-notes__flag user-notes__flag--true'
        : 'user-notes__flag user-notes__flag--false'
      const createdDate = formatDate(note.createdAt)
      const createdTime = formatTime(note.createdAt)

      return (
        <tr
          key={note.id}
          className="index-table__row user-notes__row"
          onClick={() => openViewModal(note)}
          role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openViewModal(note)
          }
        }}
      >
        <td className="index-table__cell">
          <span className={flagClass}>{note.isFlag ? 'Yes' : 'No'}</span>
        </td>
        <td className="index-table__cell">{note.authorFullName || '--'}</td>
        <td className="index-table__cell">
          <div>{createdDate}</div>
          <div className="user-notes__created-time">{createdTime}</div>
        </td>
        <td className="index-table__cell index-table__cell-clickable index-table__cell--restricted">
          {truncateText(note.text)}
        </td>
        <td className="index-table__cell">{displayType(note.notableType)}</td>
      </tr>
      )
    })
  }

  const renderModalBody = () => {
    if (modalMode === 'view' && selectedNote) {
      const createdDate = formatDate(selectedNote.createdAt)
      const createdTime = formatTime(selectedNote.createdAt)

      return (
        <div className="user-notes__modal">
          <p className="user-notes__modal-label">Created</p>
          <p className="user-notes__modal-value">{createdDate} @ {createdTime}</p>
          <p className="user-notes__modal-label">Author</p>
          <p className="user-notes__modal-value">{selectedNote.authorFullName || '--'}</p>
          <p className="user-notes__modal-label">Flagged</p>
          <p className="user-notes__modal-value">{selectedNote.isFlag ? 'Yes' : 'No'}</p>
          <p className="user-notes__modal-label">Type</p>
          <p className="user-notes__modal-value">{displayType(selectedNote.notableType)}</p>
          <p className="user-notes__modal-label">Note</p>
          <textarea
            className="user-notes__modal-text"
            aria-label="Note details"
            value={selectedNote.text}
            readOnly
          />
        </div>
      )
    }

    return (
      <div className="user-notes__modal">
        <textarea
          id="user-notes-text"
          className="user-notes__modal-text"
          aria-label="Add a Note"
          value={draftText}
          onChange={(event) => setDraftText(event.target.value)}
          rows={6}
        />
        <label className="user-notes__flagged-toggle">
          <span>Flag this note to raise visibility?</span>
          <input
            type="checkbox"
            checked={draftFlagged}
            onChange={(event) => setDraftFlagged(event.target.checked)}
          />
        </label>
        <button
          className="button button--primary"
          onClick={handleAddNote}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    )
  }

  const renderModalTitle = () => {
    if (modalMode === 'view') {
      return 'Note Details'
    }
    return 'Add a Note'
  }

  return (
    <Card title={`Notes (${meta.total})`} gridClass="user-notes">
      <div className="user-notes__toolbar">
        <button className="button button-drive-america" onClick={openCreateModal}>
          Add Note
        </button>
      </div>
      <LoadingSection isLoading={isLoading}>
        <div className="user-notes__table">
          <table className="index-table">
            <IndexHeaderRow
              headerList={headers}
              parsedSearch={{ sort, direction }}
              clickCallback={handleSortChange}
            />
            <tbody className="index-table__body">{renderRows()}</tbody>
          </table>
          {totalPages > 1 && (
            <TablePagination
              search={searchString}
              queryLength={notes.length}
              totalPages={totalPages}
              prevPageClick={handlePrevPage}
              onPageClick={handleSelectPage}
              nextPageClick={handleNextPage}
            />
          )}
        </div>
      </LoadingSection>
      {modalMode && (
        <ModalWrapper
          currentModal={modalMode}
          hideModal={closeModal}
          title={renderModalTitle()}
          width="600px"
          height="auto"
          xclose
        >
          {renderModalBody()}
        </ModalWrapper>
      )}
    </Card>
  )
}

export default UserNotes
